import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { getAllEncrypted, deleteData, STORES } from '../utils/storage';
import { ArrowLeft, DownloadSimple, Trash, BookOpen, ChartLine, FileText } from 'phosphor-react';
import { toast } from 'sonner';

const Memory = () => {
  const [diaryEntries, setDiaryEntries] = useState([]);
  const [patterns, setPatterns] = useState([]);
  const [interactions, setInteractions] = useState([]);
  const [activeTab, setActiveTab] = useState('diary');
  const [isLoading, setIsLoading] = useState(true);
  const { encryptionKey } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    loadData();
  }, [encryptionKey]);

  const loadData = async () => {
    if (!encryptionKey) return;

    setIsLoading(true);
    try {
      const [diary, patterns, interactions] = await Promise.all([
        getAllEncrypted(STORES.DIARY, encryptionKey).catch(() => []),
        getAllEncrypted(STORES.PATTERNS, encryptionKey).catch(() => []),
        getAllEncrypted(STORES.INTERACTIONS, encryptionKey).catch(() => []),
      ]);

      setDiaryEntries(diary.sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp)));
      setPatterns(patterns);
      setInteractions(interactions);
    } catch (error) {
      console.error('Load error:', error);
      toast.error('Failed to load data');
    } finally {
      setIsLoading(false);
    }
  };

  const deleteDiaryEntry = async (id) => {
    if (!window.confirm('Delete this entry? This cannot be undone.')) return;

    try {
      await deleteData(STORES.DIARY, id);
      setDiaryEntries(prev => prev.filter(e => e.id !== id));
      toast.success('Entry deleted');
    } catch (error) {
      toast.error('Failed to delete');
    }
  };

  const exportData = (type) => {
    let dataToExport;
    let filename;

    switch (type) {
      case 'diary':
        dataToExport = diaryEntries;
        filename = 'aravela_diary.json';
        break;
      case 'patterns':
        dataToExport = patterns;
        filename = 'aravela_patterns.json';
        break;
      case 'interactions':
        const summary = {
          totalInteractions: interactions.length,
          highIntensity: interactions.filter(i => i.intensity === 'high').length,
          mediumIntensity: interactions.filter(i => i.intensity === 'medium').length,
          lowIntensity: interactions.filter(i => i.intensity === 'low').length,
          interactions: interactions,
        };
        dataToExport = summary;
        filename = 'aravela_interaction_summary.json';
        break;
      default:
        return;
    }

    const blob = new Blob([JSON.stringify(dataToExport, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
    toast.success('Exported successfully');
  };

  return (
    <div className="min-h-screen bg-stone-50 noise-texture">
      {/* Header */}
      <header className="bg-white border-b border-stone-200 sticky top-0 z-10">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate('/chat')}
              className="p-2 hover:bg-stone-100 rounded-lg transition-colors"
              data-testid="back-to-chat-btn"
            >
              <ArrowLeft size={20} weight="duotone" />
            </button>
            <h1 className="font-serif font-semibold text-xl text-stone-800" data-testid="memory-title">Memory</h1>
          </div>
        </div>
      </header>

      {/* Tabs */}
      <div className="max-w-4xl mx-auto px-4 py-6">
        <div className="flex gap-2 mb-6 overflow-x-auto">
          <TabButton
            active={activeTab === 'diary'}
            onClick={() => setActiveTab('diary')}
            icon={<BookOpen size={18} weight="duotone" />}
            label="Diary"
            count={diaryEntries.length}
          />
          <TabButton
            active={activeTab === 'patterns'}
            onClick={() => setActiveTab('patterns')}
            icon={<ChartLine size={18} weight="duotone" />}
            label="Patterns"
            count={patterns.length}
          />
          <TabButton
            active={activeTab === 'interactions'}
            onClick={() => setActiveTab('interactions')}
            icon={<FileText size={18} weight="duotone" />}
            label="Summaries"
            count={interactions.length}
          />
        </div>

        {isLoading ? (
          <div className="text-center py-16 text-stone-500">Loading...</div>
        ) : (
          <div>
            {/* Diary Tab */}
            {activeTab === 'diary' && (
              <div className="space-y-4" data-testid="diary-tab">
                <div className="flex justify-between items-center mb-4">
                  <p className="text-sm text-stone-600">{diaryEntries.length} saved conversations</p>
                  {diaryEntries.length > 0 && (
                    <button
                      onClick={() => exportData('diary')}
                      className="text-sm text-primary hover:underline flex items-center gap-1"
                      data-testid="export-diary-btn"
                    >
                      <DownloadSimple size={16} weight="duotone" />
                      Export
                    </button>
                  )}
                </div>

                {diaryEntries.length === 0 ? (
                  <div className="text-center py-16 text-stone-500">
                    Nothing saved yet—your space stays private until you choose what matters.
                  </div>
                ) : (
                  diaryEntries.map(entry => (
                    <div
                      key={entry.id}
                      className="bg-white rounded-2xl p-6 shadow-sm hover:shadow-soft transition-all"
                      data-testid={`diary-entry-${entry.id}`}
                    >
                      <div className="flex justify-between items-start mb-3">
                        <span className="text-sm text-stone-500">
                          {new Date(entry.timestamp).toLocaleString()}
                        </span>
                        <button
                          onClick={() => deleteDiaryEntry(entry.id)}
                          className="text-stone-400 hover:text-red-500 transition-colors"
                          data-testid={`delete-entry-${entry.id}`}
                        >
                          <Trash size={18} weight="duotone" />
                        </button>
                      </div>
                      <div className="space-y-2 text-sm">
                        <p className="text-stone-600">{entry.messages?.length || 0} messages</p>
                      </div>
                    </div>
                  ))
                )}
              </div>
            )}

            {/* Patterns Tab */}
            {activeTab === 'patterns' && (
              <div className="space-y-4" data-testid="patterns-tab">
                <div className="flex justify-between items-center mb-4">
                  <p className="text-sm text-stone-600">{patterns.length} support patterns</p>
                  {patterns.length > 0 && (
                    <button
                      onClick={() => exportData('patterns')}
                      className="text-sm text-primary hover:underline flex items-center gap-1"
                      data-testid="export-patterns-btn"
                    >
                      <DownloadSimple size={16} weight="duotone" />
                      Export
                    </button>
                  )}
                </div>

                {patterns.length === 0 ? (
                  <div className="text-center py-16 text-stone-500">
                    No patterns recorded yet. Vela will help you build these during calm conversations.
                  </div>
                ) : (
                  <div className="bg-white rounded-2xl p-6 shadow-sm">
                    <p className="text-stone-600">Pattern tracking coming soon</p>
                  </div>
                )}
              </div>
            )}

            {/* Interactions Tab */}
            {activeTab === 'interactions' && (
              <div className="space-y-4" data-testid="interactions-tab">
                <div className="flex justify-between items-center mb-4">
                  <p className="text-sm text-stone-600">{interactions.length} interaction summaries</p>
                  {interactions.length > 0 && (
                    <button
                      onClick={() => exportData('interactions')}
                      className="text-sm text-primary hover:underline flex items-center gap-1"
                      data-testid="export-interactions-btn"
                    >
                      <DownloadSimple size={16} weight="duotone" />
                      Export
                    </button>
                  )}
                </div>

                {interactions.length === 0 ? (
                  <div className="text-center py-16 text-stone-500">
                    No interactions tracked yet
                  </div>
                ) : (
                  <div className="grid gap-4">
                    <div className="bg-white rounded-2xl p-6 shadow-sm">
                      <h3 className="font-serif font-semibold text-lg text-stone-800 mb-4">Intensity Overview</h3>
                      <div className="space-y-2 text-sm">
                        <div className="flex justify-between">
                          <span className="text-stone-600">High intensity:</span>
                          <span className="font-medium">{interactions.filter(i => i.intensity === 'high').length}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-stone-600">Medium intensity:</span>
                          <span className="font-medium">{interactions.filter(i => i.intensity === 'medium').length}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-stone-600">Low intensity:</span>
                          <span className="font-medium">{interactions.filter(i => i.intensity === 'low').length}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

const TabButton = ({ active, onClick, icon, label, count }) => {
  return (
    <button
      onClick={onClick}
      className={`flex items-center gap-2 px-4 py-2 rounded-full transition-all whitespace-nowrap ${
        active
          ? 'bg-primary text-white shadow-sm'
          : 'bg-white text-stone-600 hover:bg-stone-100'
      }`}
      data-testid={`tab-${label.toLowerCase()}`}
    >
      {icon}
      <span className="font-medium">{label}</span>
      <span className={`text-xs ${
        active ? 'text-white/80' : 'text-stone-400'
      }`}>({count})</span>
    </button>
  );
};

export default Memory;